import { useState, useEffect } from 'react';
import { ChartAttachment } from '../models/ChatMessage';
import { useDataSet } from './useDataSet';
import { applyChartMapping } from './chartMapping';

interface ProcessedData {
    data: unknown;
    options: unknown;
    type: string;
}

export const useChartData = (attachment: ChartAttachment): ProcessedData | null => {
    const [processedData, setProcessedData] = useState<ProcessedData | null>(null);
    const { getDataSetById } = useDataSet();

    useEffect(() => {
        const processData = async () => {
            try {
                // A mapping spec is required to shape the data. Chart attachments persisted before
                // #1817 carried a legacy eval'd mapping-function string instead and are skipped.
                if (!attachment.mapping) {
                    return;
                }

                // getDataSetById is typed as always returning a populated DataSetResponse, but the
                // shared fetch helper short-circuits some responses (e.g. a 429 rate-limit) to an
                // empty `{}`, so rows can be missing at runtime — guard before shaping the chart.
                const dataset = (await getDataSetById(attachment.datasetReferenceId)) as
                    { rows?: unknown[][] } | undefined;
                if (!dataset?.rows) {
                    return;
                }
                const rows: unknown[][] = dataset.rows;

                // config is plain JSON produced by the backend (ChartDesignExecutor.BuildChartConfig).
                const configObject = JSON.parse(attachment.config) as Record<string, unknown>;

                applyChartMapping(configObject, rows, attachment.mapping, attachment.chartType);

                setProcessedData({
                    options: configObject.options,
                    data: configObject.data,
                    type: configObject.type as string,
                });
            } catch (error) {
                console.error('Error processing chart data:', error);
            }
        };

        void processData();
    }, [attachment, getDataSetById]);

    return processedData;
};
